/** Límite máximo de `limit` que acepta el backend. */
export const TICKETS_MAX_PAGE_SIZE = 100;

/** Umbral (segundos) por debajo del cual se avisa de que el ticket va a caducar. */
export const TICKET_NEAR_EXPIRY_SECONDS = 24 * 60 * 60;

export const TICKET_TYPE_LABELS = {
  registration: "Registro",
  invitation: "Invitación"
};

export const PRODUCT_EMPTY_LABEL = "Sin producto";
export const CONTRACT_EMPTY_LABEL = "Sin contrato";

export const buildProductNameMap = (products = []) =>
  (Array.isArray(products) ? products : []).reduce((map, product) => {
    if (product?.id) map[product.id] = product.name || product.code || product.id;
    return map;
  }, {});

const getTicketContracts = (ticket) => (Array.isArray(ticket?.contracts) ? ticket.contracts : []);

const dedupe = (labels) => [...new Set(labels.map((label) => String(label || "").trim()).filter(Boolean))];

export const getTicketProductLabels = (ticket, productNames = {}) => {
  if (ticket?.ticket_type === "invitation") {
    if (!ticket.product_id) return [];
    return [productNames[ticket.product_id] || ticket.product_id];
  }

  // Un mismo contrato puede dar acceso a varios productos (p. ej. un pack contratado desde la
  // web), así que cada contrato aporta todos los que resolvió el backend, no solo el primero.
  return dedupe(
    getTicketContracts(ticket).flatMap((contract) =>
      (Array.isArray(contract?.products) ? contract.products : []).map((product) => product?.name)
    )
  );
};

export const getTicketContractLabels = (ticket) => {
  if (ticket?.ticket_type === "invitation") return [];

  return dedupe(getTicketContracts(ticket).map((contract) => contract?.contractDesc));
};

export const decorateTicket = (ticket, {productNames = {}, now = Date.now()} = {}) => {
  const ttl = Number.isFinite(ticket?.ttl) ? ticket.ttl : null;
  const productLabels = getTicketProductLabels(ticket, productNames);
  const contractLabels = getTicketContractLabels(ticket);

  return {
    ...ticket,
    ttl,
    // Aproximación en cliente: el backend no persiste una fecha de expiración.
    expires_at: ttl === null ? null : new Date(now + ttl * 1000).toISOString(),
    product_labels: productLabels,
    product_display: productLabels.length > 0 ? productLabels.join(", ") : PRODUCT_EMPTY_LABEL,
    contract_labels: contractLabels,
    contract_display: contractLabels.length > 0 ? contractLabels.join(", ") : CONTRACT_EMPTY_LABEL,
    near_expiry: ttl !== null && ttl < TICKET_NEAR_EXPIRY_SECONDS,
    ticket_type_label: TICKET_TYPE_LABELS[ticket?.ticket_type] || ticket?.ticket_type || "-"
  };
};

export const decorateTickets = (tickets = [], options = {}) =>
  (Array.isArray(tickets) ? tickets : []).map((ticket) => decorateTicket(ticket, options));

/** Opciones del filtro de producto: etiquetas presentes en los tickets cargados. */
export const getProductFilterOptions = (decoratedTickets = []) => {
  const labels = new Set();
  decoratedTickets.forEach((ticket) => (ticket.product_labels || []).forEach((label) => labels.add(label)));

  return [...labels].sort((a, b) => a.localeCompare(b)).map((label) => ({label, value: label}));
};

export const filterTicketsClientSide = (decoratedTickets = [], {id, product} = {}) => {
  const idTerm = String(id || "")
    .trim()
    .toLowerCase();

  return decoratedTickets.filter((ticket) => {
    if (
      idTerm &&
      !String(ticket.id || "")
        .toLowerCase()
        .includes(idTerm)
    )
      return false;
    if (product && !(ticket.product_labels || []).includes(product)) return false;
    return true;
  });
};
